import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Dialog, Grid } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrganizationNiche, OrganizationNicheStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledCard from "components/Styled/Card";
import StyledVaPage from "components/Styled/VAPage";
import { Template } from "features/Influencer/ContactList/schema";
import CreateTemplateDialog from "features/Influencer/HomePage/CreateTemplateDialog";
import CreateTemplate from "features/Influencer/Templates/TemplatesTable/CreateTemplate";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import EditFallbackTemplatesDrawer from "features/Influencer/VA/VASettings/EditFallbackTemplatesDrawer";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useBulkSend } from "utils/useBulkSend";
import { useTracking } from "utils/useTracking";

import SelfServeNicheEditDialog from "./Dialogs";
import TemplateDiversityWarningDialog from "./Dialogs/TemplateDiversityWarningDialog";
import NicheCard from "./NicheCard";
import DefaultNicheCard from "./NicheCard/DefaultNicheCard";
import styles from "./styles";

interface Props {
  needsFollowUps: boolean;
}

const SelfServeVaTemplate = ({ needsFollowUps }: Props) => {
  const navigate = useNavigate();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg, organizationBulkSendSettings } = useContext(
    OrganizationUserContext,
  );
  const { trackVaStep } = useTracking();
  const { organizationVaProfile } = useContext(OrganizationUserContext);
  const {
    outreachTemplates,
    editTemplateDialogId,
    setEditTemplateDialogId,
    createTemplateDialogOpen,
  } = useContext(OutreachTemplatesContext);
  const { fetchOrganizationBulkSendSettings, fetchNiches, settingsLoading } =
    useBulkSend();
  const { page } = useContext(OnboardingDialogsContext);
  const [openDefaultTemplateDialog, setOpenDefaultTemplateDialog] =
    useState<boolean>(false);
  const [fallbackTemplates, setFallbackTemplates] = useState<Template[]>([]);
  const [nicheEdit, setNicheEdit] = useState<OrganizationNiche | null>(null);
  const [validationLoading, setValidationLoading] = useState<boolean>(false);
  const [useOneTemplates, setUseOneTemplates] = useState<boolean>(false);
  const [showDiversityWarning, setShowDiversityWarning] =
    useState<boolean>(false);

  const activeNiches = organizationVaProfile?.niches?.filter(
    (niche) =>
      !niche?.isArchived &&
      (niche.status === OrganizationNicheStatus.active ||
        niche.status === OrganizationNicheStatus.pending),
  );

  const allNichesUseSameTemplates = () => {
    if (!activeNiches || activeNiches.length <= 1) return true;
    const firstNicheOutreachTemplateId = activeNiches[0]?.templates?.[0]?.id;
    for (let i = 1; i < activeNiches.length; i++) {
      const nicheOutreachTemplateId = activeNiches[i]?.templates?.[0]?.id;
      if (firstNicheOutreachTemplateId !== nicheOutreachTemplateId) {
        return false;
      }
    }
    return true;
  };

  const handleNextClick = () => {
    // Check if user is using one template for all niches (either explicitly or implicitly)
    const shouldShowWarning = useOneTemplates || allNichesUseSameTemplates();
    if (shouldShowWarning && activeNiches && activeNiches.length > 1) {
      trackEvent("VA Onboarding - Template Diversity Warning Shown");
      setShowDiversityWarning(true);
    } else {
      setTemplates(fallbackTemplates, useOneTemplates, true);
    }
  };

  const handleContinueAfterWarning = () => {
    setShowDiversityWarning(false);
    setTemplates(fallbackTemplates, useOneTemplates, true);
  };

  const handleEditNiche = (niche: OrganizationNiche) => {
    setNicheEdit(niche);
  };

  const handleEditDefault = () => {
    setOpenDefaultTemplateDialog(true);
  };

  const renderBody = () => {
    if (!outreachTemplates || outreachTemplates?.length === 0) {
      return (
        <>
          {page === Page.pitchType ? (
            <CreateTemplateDialog isDialog={false} />
          ) : (
            <StyledCard>
              <CreateTemplateDialog isDialog={false} />
            </StyledCard>
          )}
        </>
      );
    } else {
      return (
        <>
          <Grid container justifyContent={"center"} sx={{ mb: 4 }}>
            <Grid
              container
              justifyContent="center"
              sx={{
                backgroundColor: { xs: "none", md: "white" },
                borderRadius: 30,
                width: "max-content",
                overflow: "hidden",
              }}
            >
              <Button
                sx={[useOneTemplates ? styles.active : styles.inactive]}
                onClick={() => {
                  trackEvent("VA Onboarding - Use One Template Selected");
                  setUseOneTemplates(true);
                }}
                disabled={validationLoading}
              >
                Use one template for all niches
              </Button>
              <Button
                sx={[!useOneTemplates ? styles.active : styles.inactive]}
                onClick={() => {
                  trackEvent(
                    "VA Onboarding - Customize Template Per Niche Selected",
                  );
                  setUseOneTemplates(false);
                }}
                disabled={validationLoading}
              >
                Customize per niche
              </Button>
            </Grid>
          </Grid>

          {!useOneTemplates ? (
            <Grid
              container
              justifyContent="center"
              alignItems={"stretch"}
              gap={3}
              item
              xs={12}
            >
              {activeNiches?.map((niche) => (
                <Grid item key={niche.id}>
                  <NicheCard
                    niche={niche}
                    editNiche={handleEditNiche}
                    fallbackTemplates={fallbackTemplates}
                  />
                </Grid>
              ))}
              <DefaultNicheCard
                isFallback={true}
                editDefault={handleEditDefault}
                fallbackTemplates={fallbackTemplates}
              />
            </Grid>
          ) : (
            <Grid container justifyContent={"center"}>
              <DefaultNicheCard
                editDefault={handleEditDefault}
                fallbackTemplates={fallbackTemplates}
              />
            </Grid>
          )}
        </>
      );
    }
  };

  const setTemplates = async (
    modifiedTemplates: Template[],
    useOneTemplates?: boolean,
    moveNext?: boolean,
  ) => {
    if (validationLoading) return;
    try {
      setValidationLoading(true);
      let url = "";
      let body = {};
      if (useOneTemplates) {
        if (modifiedTemplates?.length === 0) {
          setAlert("Please set general use templates", "warning");
          return;
        }
        url = `/api/organization/${currentOrg?.id}/onboarding/templates/set-one-for-all-niches`;
        body = {
          templateIds: modifiedTemplates.map((template) => template.id),
        };
      } else if (!useOneTemplates && moveNext) {
        url = `/api/organization/${currentOrg?.id}/onboarding/templates/set-per-niche`;
      }
      if (url) {
        await fetcherAuth(getToken, url, "POST", {}, body);
        await fetchNiches();
      }
      const uniqueTemplateIds = new Set(
        activeNiches?.flatMap((niche) => niche.templates?.[0]?.id),
      );
      trackEvent("VA Onboarding - Templates Set", {
        templateCount: useOneTemplates ? 1 : uniqueTemplateIds.size,
      });
      if (moveNext) {
        navigate(`/${routes.preferencesSelfServeOnboarding}`);
        trackVaStep(routes.preferencesSelfServeOnboarding);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setValidationLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchOrganizationBulkSendSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (
      organizationBulkSendSettings?.templates &&
      organizationBulkSendSettings?.templates?.length > 0 &&
      organizationVaProfile?.niches &&
      organizationVaProfile?.niches?.length > 0
    ) {
      setFallbackTemplates(organizationBulkSendSettings?.templates);
      if (
        outreachTemplates.length === 1 &&
        !activeNiches?.[0]?.templates?.[0]?.id
      ) {
        setUseOneTemplates(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationBulkSendSettings?.templates?.length,
    organizationVaProfile?.niches?.length,
  ]);

  return (
    <>
      <StyledVaPage
        stepNumber={3}
        isInsideForm={false}
        title="Let's set up your outreach template 📝"
        description={
          outreachTemplates?.length === 0 ? (
            <>{page === Page.pitchType && "Pick an option below to start"}</>
          ) : (
            <>
              You can use one general email for your all your niches - or
              customize per niche.
              <br />
              You can always change this later in your dashboard
            </>
          )
        }
        body={
          <Box
            sx={{ mb: 4, mt: -2 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {renderBody()}
          </Box>
        }
        onBack={() => {
          if (needsFollowUps) {
            trackVaStep(routes.nichesFollowUpSelfServeOnboarding);
            navigate(`/${routes.nichesFollowUpSelfServeOnboarding}`);
            return;
          }
          trackVaStep(routes.nicheSelfServeOnboarding);
          navigate(`/${routes.nicheSelfServeOnboarding}`);
        }}
        buttonBack={
          (!outreachTemplates || outreachTemplates?.length === 0) &&
          page !== Page.pitchType
            ? ""
            : needsFollowUps
              ? "Go Back to Additional Questions"
              : "Go Back to Niches"
        }
        buttonNext={
          outreachTemplates?.length === 0 ? "" : "Continue to Preferences"
        }
        onNext={handleNextClick}
        loading={validationLoading || settingsLoading}
      />

      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}
      {createTemplateDialogOpen && <CreateTemplate />}
      {nicheEdit?.id && (
        <SelfServeNicheEditDialog
          nicheEdit={nicheEdit}
          setNicheEdit={setNicheEdit}
        />
      )}

      <Dialog open={openDefaultTemplateDialog}>
        <EditFallbackTemplatesDrawer
          open={openDefaultTemplateDialog}
          templates={fallbackTemplates}
          setTemplates={setFallbackTemplates}
          emailSettingId={organizationBulkSendSettings?.emailSettingId!}
          handleClose={() => setOpenDefaultTemplateDialog(false)}
          onSuccess={setTemplates}
          useOneTemplates={useOneTemplates}
        />
      </Dialog>

      <TemplateDiversityWarningDialog
        open={showDiversityWarning}
        onClose={() => setShowDiversityWarning(false)}
        onContinue={handleContinueAfterWarning}
        activeNiches={activeNiches || []}
        setUseOneTemplates={setUseOneTemplates}
      />
    </>
  );
};

export default SelfServeVaTemplate;
